import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { APP_NAME } from '../constants';
import { auth } from '../firebase/config';
import { 
  getCurrentUser, 
  updatePasswordWithOTP, 
  requestPasswordChangeOTP, 
  User,
  OTPResult
} from '../services/authService';
import Button from '../components/Button';
import Input from '../components/Input';
import TopBar from '../components/TopBar';

const Container = styled.div`
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (min-width: 768px) {
    padding: 0 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
`;

const Breadcrumbs = styled.div`
  display: flex;
  margin-bottom: 20px;
  font-size: 14px;
  color: #555;
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 8px;
`;

const ProfileSection = styled.div`
  margin-bottom: 40px;
  width: 100%;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #333;
`;

const FormContainer = styled.div`
  background-color: white;
  border-radius: 24px;
  padding: 32px;
  margin-bottom: 24px;
  width: 100%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const ButtonContainer = styled.div`
  margin-top: 30px;
  display: flex;
  justify-content: flex-end;
  gap: 20px;
`;

const SuccessMessage = styled.div`
  background-color: #e8f5e9;
  color: #2e7d32;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  font-family: 'Montserrat', sans-serif;
`;

const SuccessIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #4caf50;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  &::after {
    content: '✓';
    color: white;
    font-size: 12px;
    font-weight: bold;
  }
`;

const ErrorMessage = styled.div`
  background-color: #ffebee;
  color: #c62828;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  font-family: 'Montserrat', sans-serif;
`;

const ErrorIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #f44336;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  &::after {
    content: '!';
    color: white;
    font-size: 12px;
    font-weight: bold;
  }
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  color: black;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  margin-top: 10px;
  
  &:disabled {
    color: #999;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const SuccessScreen = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const SuccessIconLarge = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: #4caf50;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  
  &::after {
    content: '✓';
    color: white;
    font-size: 40px;
    font-weight: bold;
  }
`;

const SuccessTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #2e7d32;
  margin-bottom: 12px;
  font-family: 'Montserrat', sans-serif;
`;

const SuccessText = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 32px;
  line-height: 1.5;
  font-family: 'Montserrat', sans-serif;
`;

const SuccessButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
`;

const ChangePasswordScreen: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sendingOTP, setSendingOTP] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [otpResult, setOtpResult] = useState<OTPResult | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const userData = await getCurrentUser();
        if (userData) {
          setUser(userData);
          // Check if user has at least phone number or email
          if (!userData.phoneNumber && !userData.email) {
            setPasswordError('Your account does not have a phone number or email associated with it. Please update your profile first.');
          }
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  // Countdown timer for OTP resend
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [countdown]);

  const handleRequestOTP = async () => {
    setPasswordError('');
    
    if (!user || (!user.phoneNumber && !user.email)) {
      setPasswordError('Your account does not have a phone number or email associated with it. Please update your profile first.');
      return;
    }
    
    try {
      setSendingOTP(true);
      
      // Add debugging to check auth state
      console.log('User object:', user);
      console.log('Firebase auth current user:', auth.currentUser);
      console.log('Firebase auth current user uid:', auth.currentUser?.uid);
      console.log('Firebase auth current user email:', auth.currentUser?.email);
      
      // Check if auth state is properly initialized
      if (!auth.currentUser) {
        setPasswordError('Authentication state not properly initialized. Please refresh the page and try again.');
        return;
      }
      
      const result = await requestPasswordChangeOTP();
      setOtpResult(result);
      setOtpSent(true);
      setCountdown(60); // Start 60 second countdown for resend
      
      // Build success message based on what was sent
      let successMessage = 'Verification code sent to ';
      const methods = [];
      
      if (result.sms && result.phone) {
        methods.push(`phone number ${result.phone}`);
      }
      
      if (result.email && result.emailMasked) {
        methods.push(`email ${result.emailMasked}`);
      }
      
      successMessage += methods.join(' and ');
      successMessage += '. Valid for 15 minutes.';
      
      setPasswordSuccess(successMessage);
    } catch (error) {
      console.error('Error requesting OTP:', error);
      if (error instanceof Error) {
        setPasswordError(error.message);
      } else {
        setPasswordError('Failed to send verification code. Please try again.');
      }
    } finally {
      setSendingOTP(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (!user) {
      setPasswordError('Please log in again before changing your password.');
      return;
    }

    if (!otpCode) {
      setPasswordError('Please enter the verification code sent to your phone.');
      return;
    }

    if (!newPassword || newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    try {
      setChangingPassword(true);
      await updatePasswordWithOTP(user.id, otpCode, newPassword);
      
      // Set success state instead of inline message
      setPasswordChanged(true);
      
      // Clear fields
      setOtpCode('');
      setNewPassword('');
      setConfirmPassword('');
      setOtpSent(false);
      setPasswordError('');
      setPasswordSuccess('');
    } catch (error) {
      console.error('Error changing password:', error);
      
      if (error instanceof Error) {
        setPasswordError(error.message);
      } else {
        setPasswordError('An unexpected error occurred');
      }
    } finally {
      setChangingPassword(false);
    }
  };

  // Utility function to mask phone number for privacy
  const maskPhoneNumber = (phoneNumber: string): string => {
    if (!phoneNumber) return '***';
    return phoneNumber.replace(/(\d{1,2})(\d+)(\d{2})/, (_, a, b, c) => {
      return `${a}${'*'.repeat(b.length)}${c}`;
    });
  };

  // Utility function to mask email for privacy
  const maskEmail = (email: string): string => {
    if (!email) return '***';
    const [localPart, domain] = email.split('@');
    if (localPart.length <= 2) {
      return `${localPart}***@${domain}`;
    }
    return `${localPart.substring(0, 2)}***@${domain}`;
  };

  const handleNavigateToDashboard = () => {
    navigate('/dashboard');
  };

  const handleNavigateToProfile = () => {
    navigate('/profile');
  };

  if (loading) {
    return <Container>Loading...</Container>;
  }

  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Change Password</title>
        <meta name="description" content={`${APP_NAME} - Change Password`} />
      </Helmet>
      <Container>
        <ContentWrapper>
          <TopBar user={user} />
          
          <Breadcrumbs>
            <BreadcrumbLink onClick={handleNavigateToDashboard}>My Account</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbLink onClick={handleNavigateToProfile}>My Profile</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <span>Change Password</span>
          </Breadcrumbs>
          
          <ProfileSection>
            <SectionTitle>Change Password</SectionTitle>
            
            {passwordChanged ? (
              <FormContainer>
                <SuccessScreen>
                  <SuccessIconLarge />
                  <SuccessTitle>Password Updated Successfully!</SuccessTitle>
                  <SuccessText>
                    Your password has been changed successfully. You can now use your new password to sign in to your account.
                  </SuccessText>
                  <SuccessButtonContainer>
                    {/* <Button
                      text="Go to Profile"
                      onClick={handleNavigateToProfile}
                      bgColor="#4caf50"
                      textColor="#fff"
                      width="auto"
                      minWidth="150px"
                    /> */}
                    <Button
                      text="Exit"
                      onClick={handleNavigateToDashboard}
                      bgColor="#000"
                      textColor="#fff"
                      width="auto"
                      minWidth="150px"
                    />
                  </SuccessButtonContainer>
                </SuccessScreen>
              </FormContainer>
            ) : (
              <>
                {passwordSuccess && (
                  <SuccessMessage>
                    <SuccessIcon />
                    <span>{passwordSuccess}</span>
                  </SuccessMessage>
                )}
                
                {passwordError && (
                  <ErrorMessage>
                    <ErrorIcon />
                    <span>{passwordError}</span>
                  </ErrorMessage>
                )}
                
                <FormContainer>
              {!otpSent ? (
                <>
                  <p>To change your password, we'll send a verification code to your phone number and/or email address.</p>
                  {(user?.phoneNumber || user?.email) && (
                    <div style={{ marginTop: '15px' }}>
                      {user?.phoneNumber && (
                        <p>• Phone: {maskPhoneNumber(user.phoneNumber)}</p>
                      )}
                      {user?.email && (
                        <p>• Email: {maskEmail(user.email)}</p>
                      )}
                    </div>
                  )}
                  <ButtonContainer>
                    <Button
                      text="Continue" 
                      onClick={handleRequestOTP}
                      loading={sendingOTP}
                      bgColor="#000"
                      textColor="#fff"
                      width="auto"
                      minWidth="200px"
                      disabled={!user?.phoneNumber && !user?.email}
                    />
                  </ButtonContainer>
                </>
              ) : (
                <>
                  <Input 
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    placeholder="Enter verification code"
                    name="otpCode"
                    label="Verification Code"
                    required
                    secondary
                  //   maxLength={6}
                  />
                  
                  <ResendButton 
                    onClick={handleRequestOTP} 
                    disabled={countdown > 0}
                  >
                    {countdown > 0 ? `Resend code in ${countdown}s` : 'Resend code'}
                  </ResendButton>
                  
                  <Input 
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter your new password"
                    name="newPassword"
                    label="New Password"
                    required
                    secondary
                    error={passwordError && (!newPassword || newPassword.length < 6) ? 'New password must be at least 6 characters' : undefined}
                  />
                  
                  <Input 
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your new password"
                    name="confirmPassword"
                    label="Confirm New Password"
                    required
                    secondary
                    error={passwordError && newPassword !== confirmPassword ? 'Passwords do not match' : undefined}
                  />
                </>
              )}                </FormContainer>
                
                <ButtonContainer>
                  <Button 
                    text="Cancel"
                    onClick={handleNavigateToProfile}
                    bgColor="#fff"
                    textColor="#000"
                    width="auto"
                    minWidth="120px"
                  //   style={{ marginRight: '12px', border: '1px solid #ddd' }}
                  />
                  
                  {otpSent && (
                    <Button 
                      text="Save" 
                      onClick={handleChangePassword} 
                      loading={changingPassword}
                      bgColor="#000"
                      textColor="#fff"
                      width="auto"
                      minWidth="150px"
                    />
                  )}
                </ButtonContainer>
              </>
            )}
          </ProfileSection>
        </ContentWrapper>
      </Container>
    </>
  );
};

export default ChangePasswordScreen;